/**
 * Currency Formatter
 * Formats a number or numeric string as currency
 */
export const formatCurrency = (
  amount: number | string | undefined | null,
  currency: string = 'INR',
  locale: string = 'en-IN',
  symbol?: string
): string => {
  if (amount === null || amount === undefined) return `${symbol || '₹'}0.00`;

  let numericValue: number;
  if (typeof amount === 'number') {
    numericValue = amount;
  } else {
    // Remove all non-numeric characters except decimal point for parsing
    const cleaned = String(amount).replace(/[^0-9.]/g, '');
    numericValue = parseFloat(cleaned);
  }

  if (isNaN(numericValue)) return `${symbol || '₹'}0.00`;

  const formatted = new Intl.NumberFormat(locale, {
    style: symbol ? 'decimal' : 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numericValue);

  return symbol ? `${symbol}${formatted}` : formatted;
};

/**
 * Number Formatter
 * Formats a number with thousand separators
 */
export const formatNumber = (
  value: number,
  locale: string = 'en-US'
): string => {
  return new Intl.NumberFormat(locale).format(value);
};

/**
 * Percentage Formatter
 * Formats a number as a percentage
 */
export const formatPercentage = (
  value: number,
  decimals: number = 2
): string => {
  return `${value.toFixed(decimals)}%`;
};

/**
 * Compact Number Formatter
 * Formats large numbers in compact form (e.g., 1K, 1M, 1B)
 */
export const formatCompactNumber = (
  value: number,
  locale: string = 'en-US'
): string => {
  return new Intl.NumberFormat(locale, {
    notation: 'compact',
    compactDisplay: 'short',
  }).format(value);
};

/**
 * File Size Formatter
 * Formats bytes into human-readable file size
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Phone Number Formatter
 * Formats phone number in Indian format
 */
export const formatPhoneNumber = (phone: string): string => {
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');

  // Check if it's an Indian mobile number (10 digits)
  if (cleaned.length === 10) {
    return `+91 ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
  }

  // Check if it already includes country code (12 digits: 91 + 10)
  if (cleaned.length === 12 && cleaned.startsWith('91')) {
    return `+${cleaned.slice(0, 2)} ${cleaned.slice(2, 7)} ${cleaned.slice(7)}`;
  }

  // Return as is if format is not recognized
  return phone;
};

/**
 * Truncate Text
 * Truncates text to specified length with ellipsis
 */
export const truncateText = (
  text: string,
  maxLength: number = 50,
  suffix: string = '...'
): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength - suffix.length) + suffix;
};

/**
 * Capitalize First Letter
 * Capitalizes the first letter of a string
 */
export const capitalizeFirst = (text: string): string => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

/**
 * Title Case
 * Converts string to title case
 */
export const toTitleCase = (text: string): string => {
  if (!text) return '';
  return text
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Format Duration
 * Formats seconds into human-readable duration
 */
export const formatDuration = (seconds: number): string => {
  if (seconds < 60) {
    return `${seconds}s`;
  } else if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
};

/**
 * Format Date Relative
 * Formats date as relative time (e.g., "2 hours ago", "3 days ago")
 */
export const formatDateRelative = (date: Date | string): string => {
  const now = new Date();
  const then = new Date(date);
  const diffInSeconds = Math.floor((now.getTime() - then.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'just now';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 2592000) {
    const weeks = Math.floor(diffInSeconds / 604800);
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 31536000) {
    const months = Math.floor(diffInSeconds / 2592000);
    return `${months} month${months > 1 ? 's' : ''} ago`;
  } else {
    const years = Math.floor(diffInSeconds / 31536000);
    return `${years} year${years > 1 ? 's' : ''} ago`;
  }
};

/**
 * Format Date
 * Formats date in a standard format
 */
export const formatDate = (
  date: Date | string,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }
): string => {
  return new Date(date).toLocaleDateString('en-US', options);
};

/**
 * Format DateTime
 * Formats date and time in a standard format
 */
export const formatDateTime = (
  date: Date | string,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }
): string => {
  return new Date(date).toLocaleString('en-US', options);
};

/**
 * Format Time
 * Formats time in 12-hour format
 */
export const formatTime = (
  date: Date | string,
  use24Hour: boolean = false
): string => {
  return new Date(date).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: !use24Hour,
  });
};

/**
 * Get Currency Info by Country
 * Maps country codes/names to currency information
 */
export const getCurrencyByCountry = (country: string | null | undefined) => {
  if (!country) return { code: 'INR', symbol: '₹', locale: 'en-IN' };

  const countryLower = country.toLowerCase();

  // India
  if (countryLower === 'in' || countryLower === 'india') {
    return { code: 'INR', symbol: '₹', locale: 'en-IN' };
  }

  // USA
  if (countryLower === 'us' || countryLower === 'united states' || countryLower === 'usa') {
    return { code: 'USD', symbol: '$', locale: 'en-US' };
  }

  // Nigeria
  if (countryLower === 'ng' || countryLower === 'nigeria') {
    return { code: 'NGN', symbol: '₦', locale: 'en-NG' };
  }

  // Default to INR for other/unrecognized
  return { code: 'INR', symbol: '₹', locale: 'en-IN' };
};
